import { Injectable, OnDestroy, signal } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MdrPaginatedResponse } from 'src/app/model/mdr.model';
import { DEFAULT_PAGE_SIZE, DEFAULT_SORT, MdrSortState } from './list.types';

// --- LRU page cache utility -----------------------------------------------

/**
 * Bounded LRU map keyed by page number. Used by store strategies that
 * cache per-page payloads (history's full LRU, manage's backward-only LRU).
 *
 * The underlying `Map`'s insertion order *is* the recency order: `get`
 * refreshes recency via delete-then-set; `set` evicts the oldest key
 * (first one returned by `keys()`) when capacity is exceeded.
 */
export class LruPageCache<T> {
  private readonly entries = new Map<number, T>();

  constructor(private readonly maxEntries: number) {}

  get(page: number): T | undefined {
    const value = this.entries.get(page);
    if (value !== undefined) {
      this.entries.delete(page);
      this.entries.set(page, value);
    }
    return value;
  }

  has(page: number): boolean {
    return this.entries.has(page);
  }

  set(page: number, value: T): void {
    this.entries.delete(page);
    this.entries.set(page, value);
    while (this.entries.size > this.maxEntries) {
      const oldest = this.entries.keys().next().value;
      if (oldest === undefined) break;
      this.entries.delete(oldest);
    }
  }

  delete(page: number): boolean {
    return this.entries.delete(page);
  }

  /**
   * Removes every entry whose page key satisfies `shouldDelete`. Use for
   * targeted invalidation (e.g. dropping pages affected by a mutation).
   */
  prune(shouldDelete: (page: number) => boolean): void {
    for (const page of [...this.entries.keys()]) {
      if (shouldDelete(page)) this.entries.delete(page);
    }
  }

  clear(): void {
    this.entries.clear();
  }
}

// --- Store -----------------------------------------------------------------

/**
 * Generic base for paginated list stores backed by `MdrResponse`. Owns the
 * common machinery: public signals consumed by the template, filter/sort
 * state, foreground HTTP load with cancellation tokens, and a small set of
 * hooks that subclasses use to plug in their *buffering strategy*.
 *
 * The base intentionally knows **nothing** about FIFO queues, page caches
 * or prefetching. Each strategy lives entirely in its subclass:
 *  - manage (FIFO buffer): contiguous-after-currentPage queue with
 *    hysteresis refill, plus optimistic row consume on Accept/Reject.
 *  - history (LRU page cache): per-page Map serving forward and backward
 *    navigation symmetrically, plus background prefetch ahead.
 *
 * Subclasses must implement `goToPage` (the strategy-specific fast path),
 * `fetchPage` (the concrete backend call) and `keyOf` (the stable row key).
 * The optional hooks `onPageLoaded` and `onClearVolatileState` let them
 * participate in the load and reset lifecycles.
 *
 * `TItem` is the row type (e.g. `MdrOpenRequest`, `RestaurantListItem`);
 * `TFilters` is the strategy-specific filter shape.
 */
@Injectable()
export abstract class MdrPaginatedListStore<TItem, TFilters extends object> implements OnDestroy {
  // --- Public state (consumed by component / template) ---------------------

  /** Rows currently visible in the table. */
  readonly displayedItems = signal<TItem[]>([]);
  /** 1-based page currently displayed. */
  readonly currentPage = signal<number>(1);
  /** Current paginator page size. */
  readonly pageSize = signal<number>(DEFAULT_PAGE_SIZE);
  /** Total number of server-side requests (drives the paginator). */
  readonly totalItems = signal<number>(0);
  /** True while a foreground request is in flight (drives the spinner). */
  readonly loading = signal<boolean>(false);
  /**
   * True after the most recent reload completes; flipped back to false at
   * the start of any filter/sort/pageSize change. Used to suppress the
   * row-enter animation while rows are streaming in.
   */
  readonly firstLoadComplete = signal<boolean>(false);
  /**
   * Last foreground load error (null on success). Components watch this to
   * surface modals/toasts. Prefetch errors are intentionally swallowed
   * (best-effort, no UX disruption) and never set this signal.
   */
  readonly loadError = signal<unknown | null>(null);

  // --- Protected state (visible to subclasses) -----------------------------

  protected readonly filters = signal<TFilters>({} as TFilters);
  protected readonly sort = signal<MdrSortState>(DEFAULT_SORT);
  /** True while a background prefetch is in flight. Subclasses drive it. */
  protected readonly prefetching = signal<boolean>(false);

  // --- Cancellation infrastructure -----------------------------------------

  protected readonly destroy$ = new Subject<void>();
  protected loadSub?: Subscription;
  /** Subclass-owned: subclasses assign here when starting a prefetch. */
  protected prefetchSub?: Subscription;
  /**
   * Monotonic token incremented on every `cancelInflight`. In-flight
   * responses (load or prefetch) compare their captured value against the
   * current one before writing; mismatches are discarded.
   */
  protected generation = 0;

  // --- Track-by ------------------------------------------------------------

  /** Strategy-specific stable key for a row (e.g. its id). */
  protected abstract keyOf(item: TItem): string;

  /** Stable trackBy for `*matRowDef` — preserves DOM identity across swaps. */
  readonly trackById = (_index: number, row: TItem): string => this.keyOf(row);

  // --- Lifecycle -----------------------------------------------------------

  ngOnDestroy(): void {
    this.cancelInflight();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- Public API ----------------------------------------------------------

  /** Bootstraps the store: sets the initial filters/sort and loads page 1. */
  init(filters: TFilters, sort: MdrSortState, pageSize?: number): void {
    if (pageSize !== undefined && Number.isInteger(pageSize) && pageSize > 0) {
      this.pageSize.set(pageSize);
    }
    this.filters.set({ ...filters });
    this.sort.set({ ...sort });
    this.restartFromFirstPage();
  }

  /** Applies new filters and restarts from page 1. */
  setFilters(next: TFilters): void {
    this.filters.set({ ...next });
    this.restartFromFirstPage();
  }

  /** Applies a new sort and restarts from page 1. */
  setSort(next: MdrSortState): void {
    this.sort.set({ ...next });
    this.restartFromFirstPage();
  }

  /** Changes the page size and restarts from page 1. No-op if unchanged or invalid. */
  setPageSize(size: number): void {
    if (!Number.isInteger(size) || size <= 0 || size === this.pageSize()) return;
    this.pageSize.set(size);
    this.restartFromFirstPage();
  }

  // --- Subclass extension points ------------------------------------------

  /**
   * Strategy-specific navigation. Subclasses decide when to serve from
   * their own buffer/cache and when to fall through to `loadPage`.
   */
  abstract goToPage(target: number): void;

  /**
   * Concrete backend call. Subclasses pull from `filters()`, `sort()` and
   * `pageSize()` to assemble parameters for the right service method.
   */
  protected abstract fetchPage(page: number): Observable<MdrPaginatedResponse<TItem>>;

  /**
   * Hook called after a successful foreground load. Default no-op.
   * Subclasses that maintain a page cache write to it here.
   */
  protected onPageLoaded(_page: number, _items: TItem[]): void {}

  /**
   * Hook called from `restartFromFirstPage` before reloading page 1.
   * Subclasses clear their strategy-specific state (FIFO buffer, page
   * cache, pendingActions, latches) here.
   */
  protected onClearVolatileState(): void {}

  /**
   * Hook called from `restartFromFirstPage` *immediately after* firing the
   * HTTP for page 1 — before its response arrives. Subclasses that want
   * to warm the buffer (e.g. eager prefetch of page 2 in parallel) do it
   * here. Default no-op.
   */
  protected onWarmStart(): void {}

  // --- Protected: shared internals ----------------------------------------

  /**
   * Foreground HTTP load with cancellation. On success: replaces displayed,
   * updates totalItems and currentPage, flips `firstLoadComplete`, and
   * calls `onPageLoaded`. Stale responses (after a filter/sort/page
   * change) are discarded via the captured generation token.
   */
  protected loadPage(page: number): void {
    this.cancelInflight();
    this.loading.set(true);
    const gen = this.generation;

    this.loadSub = this.fetchPage(page)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: MdrPaginatedResponse<TItem>) => {
          if (gen !== this.generation) {
            this.loading.set(false);
            return;
          }
          const items = res.data ?? [];
          this.displayedItems.set(items);
          this.totalItems.set(res.pagination?.total ?? 0);
          this.currentPage.set(page);
          this.loading.set(false);
          this.loadError.set(null);
          this.firstLoadComplete.set(true);
          this.onPageLoaded(page, items);
        },
        error: (err: unknown) => {
          if (gen !== this.generation) {
            this.loading.set(false);
            return;
          }
          this.loading.set(false);
          this.loadError.set(err ?? new Error('Unknown load error'));
        },
      });
  }

  /**
   * Shared pattern for `init`, `setFilters`, `setSort`, `setPageSize`:
   * abort in-flight, clear pagination signals, let the subclass clear its
   * own state, then reload page 1.
   */
  protected restartFromFirstPage(): void {
    this.cancelInflight();
    this.displayedItems.set([]);
    this.currentPage.set(1);
    this.totalItems.set(0);
    this.firstLoadComplete.set(false);
    this.onClearVolatileState();
    this.loadPage(1);
    this.onWarmStart();
  }

  /**
   * Cancels foreground load and any in-flight prefetch, resets loading
   * flags, and bumps the generation token. Safe to call repeatedly.
   */
  protected cancelInflight(): void {
    this.loadSub?.unsubscribe();
    this.prefetchSub?.unsubscribe();
    this.loadSub = undefined;
    this.prefetchSub = undefined;
    this.loading.set(false);
    this.prefetching.set(false);
    this.generation++;
  }

  /**
   * Cancels only the in-flight prefetch (not the foreground load). Use when
   * state changes (cache-hit navigation, optimistic local consume) make a
   * pending prefetch's payload stale without affecting an in-flight load.
   *
   * NOTE: does NOT bump `generation` — bumping would also invalidate any
   * concurrent `loadSub` whose callback gen-checks against the same token.
   * Unsubscribing tears down the HTTP request and prevents the `next`
   * callback from firing, which is sufficient to keep the buffer from being
   * contaminated by stale rows.
   */
  protected cancelPrefetchOnly(): void {
    this.prefetchSub?.unsubscribe();
    this.prefetchSub = undefined;
    this.prefetching.set(false);
  }

  /**
   * Shared scaffolding for background prefetches: cancels any prior prefetch,
   * captures `generation` for stale-response rejection, flips the
   * `prefetching` flag, and invokes `applyItems` with the response payload
   * only when the generation token still matches.
   *
   * INVARIANT: `applyItems` runs BEFORE `prefetching.set(false)` — refill
   * effects re-evaluate on that flag, and reordering would let them read a
   * stale buffer length and launch a redundant fetch.
   */
  protected runPrefetch(page: number, applyItems: (items: TItem[]) => void): void {
    const gen = this.generation;
    this.prefetchSub?.unsubscribe();
    this.prefetching.set(true);

    this.prefetchSub = this.fetchPage(page)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: MdrPaginatedResponse<TItem>) => {
          if (gen !== this.generation) {
            this.prefetching.set(false);
            return;
          }
          applyItems(res.data ?? []);
          this.prefetching.set(false);
        },
        error: () => {
          this.prefetching.set(false);
        },
      });
  }
}
